import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";

interface Product {
  id: string;
  name: string;
  category: string;
  case_configuration: string;
  oz_on_hand: number;
  oz_per_case: number;
  vendor_name: string;
  system_cases: number;
}

interface CountResult {
  previous_oz: number;
  new_oz: number;
  variance_oz: number;
  variance_dollars: number;
}

/**
 * Group a multi-match lookup response by product. Counting is product-level,
 * so formats of the same product collapse into one choice; distinct products
 * become picker entries the counter must choose between.
 */
export function groupFormatsByProduct(
  formats: Array<{ product_id: string; product_name: string; name: string }>
): Array<{ id: string; name: string; formats: string }> {
  const byProduct = new Map<string, { id: string; name: string; formats: string }>();
  for (const f of formats) {
    const existing = byProduct.get(f.product_id);
    if (existing) {
      existing.formats += `, ${f.name}`;
    } else {
      byProduct.set(f.product_id, { id: f.product_id, name: f.product_name, formats: f.name });
    }
  }
  return Array.from(byProduct.values());
}

export default function MobileScanCount() {
  const [scanning, setScanning] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [cases, setCases] = useState("");
  const [loose, setLoose] = useState("");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<CountResult | null>(null);
  const [error, setError] = useState("");
  const [manualSearch, setManualSearch] = useState("");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [productChoices, setProductChoices] = useState<Array<{ id: string; name: string; formats: string }>>([]);
  const [recentCounts, setRecentCounts] = useState<Array<{ name: string; cases: string; loose: string; variance: number }>>([]);
  const videoRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<unknown>(null);

  // Start camera scanner
  const startScanner = useCallback(async () => {
    setScanning(true);
    setError("");
    setProduct(null);
    setResult(null);

    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("scanner-region");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" }, // back camera
        {
          fps: 10,
          qrbox: { width: 280, height: 100 },
          aspectRatio: 3,
        },
        async (decodedText: string) => {
          // Got a barcode!
          await scanner.stop();
          setScanning(false);
          await lookupProduct(decodedText);
        },
        () => {} // ignore scan errors (no code found yet)
      );
    } catch (err) {
      setError("Camera access denied or not available. Use manual search below.");
      setScanning(false);
    }
  }, []);

  const stopScanner = useCallback(async () => {
    try {
      if (scannerRef.current) {
        await (scannerRef.current as { stop: () => Promise<void> }).stop();
      }
    } catch {}
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, [stopScanner]);

  // Load full product details by product id
  const loadProductById = async (productId: string) => {
    setError("");
    setProductChoices([]);
    try {
      const prodRes = await api.get(`/products/${productId}`);
      const p = prodRes.data;
      setProduct({
        id: p.id,
        name: p.name,
        category: p.category,
        case_configuration: p.case_configuration || "",
        oz_on_hand: Number(p.oz_on_hand),
        oz_per_case: Number(p.oz_per_case),
        vendor_name: p.vendor_name || "",
        system_cases: Number(p.oz_per_case) > 0 ? Math.round(Number(p.oz_on_hand) / Number(p.oz_per_case) * 10) / 10 : 0,
      });
      setCases("");
      setLoose("");
      setNote("");
      setResult(null);
    } catch {
      setError("Could not load product details.");
    }
  };

  // Look up product by UPC. Shared UPCs are legitimate (breweries reuse
  // codes; tiers share the can's code), so a scan can match several
  // formats — possibly across different products. Counting is per product:
  // one product means proceed; several means the counter picks, never guess.
  const lookupProduct = async (upc: string) => {
    setError("");
    try {
      const res = await api.get(`/products/lookup/${upc.trim()}`);
      const item = res.data;
      if (item.multiple && Array.isArray(item.formats)) {
        const choices = groupFormatsByProduct(item.formats);
        if (choices.length === 1) {
          await loadProductById(choices[0].id);
        } else {
          setProductChoices(choices);
        }
        return;
      }
      await loadProductById(item.product_id || item.id);
    } catch {
      setError(`Product not found for barcode: ${upc}`);
    }
  };

  // Manual search
  const handleSearch = async (query: string) => {
    setManualSearch(query);
    if (query.length < 2) { setSearchResults([]); return; }
    try {
      const res = await api.get(`/products?search=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(res.data.map((p: Record<string, unknown>) => ({
        id: p.id,
        name: p.name,
        category: p.category,
        case_configuration: p.case_configuration || "",
        oz_on_hand: Number(p.oz_on_hand),
        oz_per_case: Number(p.oz_per_case),
        vendor_name: "",
        system_cases: Number(p.oz_per_case) > 0 ? Math.round(Number(p.oz_on_hand) / Number(p.oz_per_case) * 10) / 10 : 0,
      })));
    } catch { setSearchResults([]); }
  };

  const selectProduct = (p: Product) => {
    setProduct(p);
    setSearchResults([]);
    setManualSearch("");
    setCases("");
    setLoose("");
    setNote("");
    setResult(null);
  };

  // Submit count
  const submitCount = async () => {
    if (!product) return;
    setError("");
    try {
      const res = await api.post("/inventory-counts/quick", {
        product_id: product.id,
        counted_cases: Number(cases) || 0,
        counted_loose_units: Number(loose) || 0,
        note: note || "Mobile scan count",
      });
      setResult(res.data);
      setRecentCounts(prev => [{
        name: product.name,
        cases: cases || "0",
        loose: loose || "0",
        variance: res.data.variance_oz,
      }, ...prev.slice(0, 9)]);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Count failed";
      setError(msg);
    }
  };

  // Parse case config for display
  const getUnitLabel = (config: string): string => {
    const multi = config.match(/(\d+)\/(\d+\.?\d*)\s*OZ\s*(CAN|BTL|BOTTLE)?/i);
    if (multi) return `${multi[2]}oz ${(multi[3] || "unit").toLowerCase()}s`;
    const single = config.match(/(\d+\.?\d*)\s*OZ/i);
    if (single) return `${single[1]}oz units`;
    return "units";
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--rbw-bg)" }}>
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between" style={{ background: "var(--rbw-navy)" }}>
        <h1 className="text-lg font-bold" style={{ color: "var(--rbw-gold)" }}>Scan & Count</h1>
        <a href="/inventory" className="text-sm" style={{ color: "var(--rbw-gold)" }}>Back to Inventory</a>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Scanner */}
        {!product && !result && productChoices.length === 0 && (
          <>
            {scanning ? (
              <div className="rounded-xl overflow-hidden" style={{ border: "3px solid var(--rbw-gold)" }}>
                <div id="scanner-region" ref={videoRef} style={{ width: "100%" }} />
                <button onClick={stopScanner}
                  className="w-full py-3 text-sm font-bold" style={{ background: "var(--rbw-navy)", color: "var(--rbw-gold)" }}>
                  Stop Scanner
                </button>
              </div>
            ) : (
              <button onClick={startScanner}
                className="w-full py-6 rounded-xl text-lg font-bold transition-all active:scale-95"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                Tap to Scan Barcode
              </button>
            )}

            {/* Manual search */}
            <div>
              <input
                type="text"
                value={manualSearch}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Or search by name..."
                className="w-full px-4 py-3 rounded-xl border text-sm"
                style={{ borderColor: "var(--rbw-border)" }}
              />
              {searchResults.length > 0 && (
                <div className="mt-1 rounded-xl border overflow-hidden" style={{ borderColor: "var(--rbw-border)", background: "white" }}>
                  {searchResults.map(p => (
                    <button key={p.id} onClick={() => selectProduct(p)}
                      className="w-full px-4 py-3 text-left border-b text-sm hover:bg-gray-50"
                      style={{ borderColor: "var(--rbw-border)" }}>
                      <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{p.name}</div>
                      <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                        {p.case_configuration} | System: {p.system_cases} cases | {p.category}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Shared UPC — several products match, counter picks */}
        {productChoices.length > 0 && !product && (
          <div className="rounded-xl p-4" style={{ background: "white", border: "2px solid var(--rbw-gold)" }}>
            <h2 className="text-base font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
              This barcode is on {productChoices.length} products
            </h2>
            <div className="text-xs mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              Shared UPCs are normal — pick the product you are counting.
            </div>
            {productChoices.map((c) => (
              <button key={c.id} onClick={() => loadProductById(c.id)}
                className="w-full px-4 py-3 text-left border rounded-lg mb-2 text-sm hover:bg-gray-50"
                style={{ borderColor: "var(--rbw-border)" }}>
                <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>{c.name}</div>
                <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{c.formats}</div>
              </button>
            ))}
            <button onClick={() => setProductChoices([])}
              className="w-full py-2 rounded-lg font-bold text-sm mt-1"
              style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
              Cancel
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-3 rounded-lg text-sm" style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
            {error}
            <button onClick={() => setError("")} className="float-right font-bold">&times;</button>
          </div>
        )}

        {/* Product found — enter count */}
        {product && !result && (
          <div className="rounded-xl p-4" style={{ background: "white", border: "1px solid var(--rbw-border)" }}>
            <div className="mb-4">
              <h2 className="text-lg font-bold" style={{ color: "var(--rbw-navy)" }}>{product.name}</h2>
              <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
                {product.case_configuration} | {product.category}
              </div>
              <div className="mt-2 px-3 py-2 rounded-lg text-sm" style={{ background: "var(--rbw-bg)" }}>
                System thinks: <strong>{product.system_cases} cases</strong> ({Math.round(product.oz_on_hand)} oz)
              </div>
            </div>

            {/* Count inputs */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
                  Full Cases
                </label>
                <input type="number" inputMode="numeric" value={cases} onChange={e => setCases(e.target.value)}
                  className="w-full px-4 py-4 rounded-xl border text-2xl text-center font-bold"
                  style={{ borderColor: "var(--rbw-border)", color: "var(--rbw-navy)" }}
                  placeholder="0" autoFocus min="0" />
              </div>
              <div>
                <label className="block text-xs font-bold mb-1" style={{ color: "var(--rbw-navy)" }}>
                  Loose {getUnitLabel(product.case_configuration)}
                </label>
                <input type="number" inputMode="numeric" value={loose} onChange={e => setLoose(e.target.value)}
                  className="w-full px-4 py-4 rounded-xl border text-2xl text-center font-bold"
                  style={{ borderColor: "var(--rbw-border)", color: "var(--rbw-navy)" }}
                  placeholder="0" min="0" />
              </div>
            </div>

            <input type="text" value={note} onChange={e => setNote(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm mb-3"
              style={{ borderColor: "var(--rbw-border)" }}
              placeholder="Note (optional)" />

            <div className="flex gap-3">
              <button onClick={() => { setProduct(null); setResult(null); }}
                className="flex-1 py-3 rounded-xl font-bold text-sm"
                style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                Cancel
              </button>
              <button onClick={submitCount}
                className="flex-1 py-3 rounded-xl font-bold text-sm transition-all active:scale-95"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                Save Count
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && product && (
          <div className="rounded-xl p-4 text-center" style={{ background: "white", border: "1px solid var(--rbw-border)" }}>
            <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-xl"
              style={{ background: "var(--rbw-success)", color: "#fff" }}>
              &#10003;
            </div>
            <h3 className="font-bold text-lg mb-1" style={{ color: "var(--rbw-navy)" }}>{product.name}</h3>
            <div className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>Count saved</div>

            <div className="rounded-lg p-3 mb-3 text-sm text-left" style={{ background: "var(--rbw-bg)" }}>
              <div className="flex justify-between mb-1">
                <span>Was:</span>
                <span className="font-bold">{(result.previous_oz / (product.oz_per_case || 1)).toFixed(1)} cases</span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Now:</span>
                <span className="font-bold">{(result.new_oz / (product.oz_per_case || 1)).toFixed(1)} cases</span>
              </div>
              <div className="flex justify-between pt-1" style={{ borderTop: "1px solid var(--rbw-border)" }}>
                <span>Variance:</span>
                <span className="font-bold" style={{
                  color: result.variance_oz === 0 ? "var(--rbw-gray-dark)" :
                    result.variance_oz > 0 ? "var(--rbw-success)" : "var(--rbw-danger)"
                }}>
                  {result.variance_oz > 0 ? "+" : ""}{(result.variance_oz / (product.oz_per_case || 1)).toFixed(1)} cases
                  ({result.variance_dollars >= 0 ? "+$" : "-$"}{Math.abs(result.variance_dollars).toFixed(2)})
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => { setProduct(null); setResult(null); }}
                className="flex-1 py-3 rounded-xl font-bold text-sm transition-all active:scale-95"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                Scan Next
              </button>
            </div>
          </div>
        )}

        {/* Recent counts in this session */}
        {recentCounts.length > 0 && (
          <div className="rounded-xl p-3" style={{ background: "white", border: "1px solid var(--rbw-border)" }}>
            <h3 className="text-xs font-bold uppercase mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
              Counted this session ({recentCounts.length})
            </h3>
            {recentCounts.map((c, i) => (
              <div key={i} className="flex justify-between text-xs py-1"
                style={{ borderBottom: i < recentCounts.length - 1 ? "1px solid var(--rbw-border)" : "none" }}>
                <span className="truncate flex-1" style={{ color: "var(--rbw-navy)" }}>{c.name}</span>
                <span className="ml-2" style={{ color: "var(--rbw-gray-dark)" }}>{c.cases}c + {c.loose}l</span>
                <span className="ml-2 font-bold" style={{
                  color: c.variance === 0 ? "var(--rbw-gray)" :
                    c.variance > 0 ? "var(--rbw-success)" : "var(--rbw-danger)",
                  minWidth: "40px", textAlign: "right"
                }}>
                  {c.variance === 0 ? "=" : c.variance > 0 ? "+" : ""}{c.variance.toFixed(0)}oz
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
